import { Fragment, useEffect, useRef, useState } from 'react'
import { Capacitor } from '@capacitor/core'
import { App as CapacitorApp } from '@capacitor/app'
import HomeTab from './HomeTab'
import MessagesTab from './MessagesTab'
import ProfileTab from './ProfileTab'
import StempelkaartTab from './StempelkaartTab'
import TeamTab from './TeamTab'
import { supabase } from '../lib/supabase'
import { wisWebMeldingen } from '../pushNotifications'

const TABS = [
  {
    id: 'news', label: 'Home',
    icon: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><rect x="3" y="4" width="18" height="16" rx="2"/><line x1="7" y1="9" x2="17" y2="9"/><line x1="7" y1="13" x2="14" y2="13"/></svg>
  },
  {
    id: 'messages', label: 'Berichten',
    icon: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z"/></svg>
  },
  {
    id: 'stempel', label: 'Fase',
    icon: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><rect x="3" y="3" width="7" height="7" rx="1.5"/><rect x="14" y="3" width="7" height="7" rx="1.5"/><rect x="3" y="14" width="7" height="7" rx="1.5"/><rect x="14" y="14" width="7" height="7" rx="1.5"/></svg>
  },
  {
    id: 'team', label: 'Team',
    icon: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><circle cx="9" cy="7" r="3"/><path d="M3 21v-2a4 4 0 014-4h4a4 4 0 014 4v2"/><circle cx="18" cy="7" r="2"/><path d="M21 21v-2a3 3 0 00-2-2.83"/></svg>
  },
  {
    id: 'profile', label: 'Profiel',
    icon: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><circle cx="12" cy="8" r="4"/><path d="M4 20c0-4 3.6-7 8-7s8 3 8 7"/></svg>
  },
]

// Geeft een leesbaar daglabel: "Vandaag", "Gisteren" of bv. "9 juni"
function dagLabel(datum) {
  const dt = new Date(datum)
  const nu = new Date()
  const zelfdeDag = (a, b) => a.toDateString() === b.toDateString()
  const gisteren = new Date(nu)
  gisteren.setDate(nu.getDate() - 1)
  if (zelfdeDag(dt, nu)) return 'Vandaag'
  if (zelfdeDag(dt, gisteren)) return 'Gisteren'
  return dt.toLocaleDateString('nl-NL', {
    day: 'numeric',
    month: 'long',
    ...(dt.getFullYear() !== nu.getFullYear() ? { year: 'numeric' } : {}),
  })
}

// Berichten tab als volledig aparte pagina — buiten app-shell zodat
// het iOS toetsenbord de layout niet verstoort
function BerichtenPagina({ member, onBack, onNavigate, ongelezen, setOngelezen }) {
  const [messages, setMessages] = useState([])
  const [text, setText] = useState([])
  const [tekst, setTekst] = useState('')
  const [loading, setLoading] = useState(true)
  const [sending, setSending] = useState(false)
  const [inputFocused, setInputFocused] = useState(false)
  const bottomRef = useRef(null)

  useEffect(() => {
    laadBerichten()
    // Markeer als gelezen
    supabase.from('messages')
      .update({ gelezen: true })
      .eq('member_id', member.member_id)
      .eq('from_admin', true)
      .eq('gelezen', false)
      .then(() => setOngelezen(0))

    // Realtime
    const channel = supabase
      .channel(`msg:${member.member_id}`)
      .on('postgres_changes', {
        event: 'INSERT', schema: 'public', table: 'messages',
        filter: `member_id=eq.${member.member_id}`,
      }, (payload) => {
        setMessages(prev => prev.some(m => m.id === payload.new.id) ? prev : [...prev, payload.new])
      })
      .subscribe()
    return () => channel.unsubscribe()
  }, [member.member_id])

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [messages])

  async function laadBerichten() {
    const { data } = await supabase
      .from('messages')
      .select('*')
      .eq('member_id', member.member_id)
      .order('created_at', { ascending: true })
    setMessages(data ?? [])
    setLoading(false)
  }

  async function verstuur(e) {
    e.preventDefault()
    if (!tekst.trim() || sending) return
    setSending(true)
    try {
      await supabase.from('messages').insert({
        member_id: member.member_id,
        from_admin: false,
        text: tekst.trim(),
      })
      setTekst('')
    } catch { /* ignore */ }
    finally { setSending(false) }
  }

  // Volledige pagina — geen fixed positioning, gewone scroll
  // zodat iOS toetsenbord gewoon de pagina omhoog duwt
  return (
    <div style={{
      position: 'fixed',
      top: 0, bottom: 0,
      left: '50%', transform: 'translateX(-50%)',
      width: '100%', maxWidth: 390,
      background: 'var(--bg)',
      display: 'flex', flexDirection: 'column',
    }}>
      {/* Header */}
      <div className="top-bar" style={{ flexShrink: 0 }}>
        <button
          onClick={onBack}
          aria-label="Terug naar de app"
          style={{ display: 'flex', alignItems: 'center', gap: 5, background: 'rgba(255,255,255,0.18)', border: 'none', color: '#ffffff', fontSize: 13, cursor: 'pointer', fontFamily: 'Montserrat', fontWeight: 700, letterSpacing: '0.01em', borderRadius: 16, padding: '6px 12px' }}
        >
          ← Terug
        </button>
        <div style={{ textAlign: 'center', lineHeight: 1.2 }}>
          <div style={{ color: '#fff', fontFamily: 'Montserrat', fontWeight: 800, fontSize: 14, letterSpacing: '0.02em' }}>Berichten</div>
          <div style={{ color: 'rgba(255,255,255,0.78)', fontSize: 10 }}>Balie Vitaal Sterk</div>
        </div>
        <img src="/logo.png" alt="Vitaal Sterk" style={{ height: 30, objectFit: 'contain' }} />
      </div>

      {/* Berichten lijst — scrollbaar */}
      <div style={{ flex: 1, overflowY: 'auto', padding: 14, display: 'flex', flexDirection: 'column', gap: 2 }}>
        {loading && <div className="spinner" />}
        {!loading && messages.length === 0 && (
          <p style={{ textAlign: 'center', color: 'var(--muted)', fontSize: 13, marginTop: 32 }}>
            Nog geen berichten. Stuur ons een bericht!
          </p>
        )}
        {(() => {
          // Index van het laatste door het lid verstuurde bericht (voor leesbevestiging)
          let laatsteEigenIndex = -1
          messages.forEach((m, i) => { if (!m.from_admin) laatsteEigenIndex = i })
          let vorigeDag = null
          return messages.map((msg, i) => {
            const dezeDag = new Date(msg.created_at).toDateString()
            const toonScheider = dezeDag !== vorigeDag
            vorigeDag = dezeDag
            return (
              <Fragment key={msg.id}>
                {toonScheider && (
                  <div style={{ alignSelf: 'center', background: 'var(--green-light)', color: 'var(--green-dark)', fontSize: 11, fontWeight: 700, fontFamily: 'Montserrat', padding: '3px 12px', borderRadius: 12, margin: '12px 0 6px' }}>
                    {dagLabel(msg.created_at)}
                  </div>
                )}
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: msg.from_admin ? 'flex-start' : 'flex-end' }}>
                  <div className={`msg-bubble ${msg.from_admin ? 'msg-in' : 'msg-out'}`}>{msg.text}</div>
                  <span className="msg-time" style={{ margin: '2px 4px 6px' }}>
                    {new Date(msg.created_at).toLocaleTimeString('nl-NL', { hour: '2-digit', minute: '2-digit' })}
                    {!msg.from_admin && i === laatsteEigenIndex && (msg.gelezen ? ' · Gelezen' : ' · Verzonden')}
                  </span>
                </div>
              </Fragment>
            )
          })
        })()}
        <div ref={bottomRef} />
      </div>

      {/* Invoerveld — normaal in de flow, niet fixed */}
      <form onSubmit={verstuur} style={{
        flexShrink: 0,
        padding: '10px 14px',
        paddingBottom: inputFocused ? 'max(10px, env(safe-area-inset-bottom))' : '10px',
        borderTop: '1px solid var(--border)',
        background: 'var(--white)',
        display: 'flex', gap: 8,
      }}>
        <input
          value={tekst}
          onChange={e => setTekst(e.target.value)}
          onFocus={() => setInputFocused(true)}
          onBlur={() => setInputFocused(false)}
          placeholder="Stuur een bericht…"
          aria-label="Typ een bericht aan de balie"
          style={{ flex: 1, borderRadius: 20, padding: '9px 14px' }}
        />
        <button
          type="submit"
          disabled={sending || !tekst.trim()}
          aria-label="Bericht versturen"
          style={{
            background: 'var(--green)', color: '#fff', border: 'none',
            borderRadius: 20, padding: '9px 16px', fontWeight: 700,
            fontSize: 13, cursor: 'pointer', fontFamily: 'Montserrat',
            opacity: !tekst.trim() ? 0.5 : 1, flexShrink: 0
          }}
        >
          {sending ? '…' : 'Stuur'}
        </button>
      </form>

      {/* Onderbalk — zelfde navigatie als de rest van de app.
          Verbergt zich tijdens typen zodat het toetsenbord het invoerveld niet afdekt. */}
      {!inputFocused && (
        <nav className="bottom-nav" style={{ flexShrink: 0 }}>
          {TABS.map(tab => (
            <button
              key={tab.id}
              className={tab.id === 'messages' ? 'active' : ''}
              onClick={() => { if (tab.id !== 'messages') onNavigate(tab.id) }}
              style={{ position: 'relative' }}
            >
              {tab.icon}
              {tab.label}
            </button>
          ))}
        </nav>
      )}
    </div>
  )
}

export default function MemberApp({ member, onSignOut }) {
  const [activeTab, setActiveTab] = useState('news')
  const [memberData, setMemberData] = useState(member)
  const [ongelezen, setOngelezen] = useState(0)

  useEffect(() => {
    laadOngelezen()
    const channel = supabase
      .channel(`badge:${member.member_id}`)
      .on('postgres_changes', {
        event: 'INSERT', schema: 'public', table: 'messages',
        filter: `member_id=eq.${member.member_id}`,
      }, (payload) => {
        if (payload.new.from_admin) setOngelezen(prev => prev + 1)
      })
      .subscribe()

    // Realtime staat stil zolang de app op de achtergrond zit — een bericht dat
    // binnenkomt terwijl het scherm vergrendeld is, wordt dan gemist. Daarom bij
    // het weer actief worden van de app het ongelezen-aantal opnieuw ophalen.
    let resumeListener
    if (Capacitor.isNativePlatform()) {
      CapacitorApp.addListener('appStateChange', ({ isActive }) => {
        if (isActive) laadOngelezen()
      }).then(l => { resumeListener = l })
    } else {
      wisWebMeldingen()
      document.addEventListener('visibilitychange', handleVisibility)
    }

    function handleVisibility() {
      if (document.visibilityState === 'visible') {
        laadOngelezen()
        wisWebMeldingen()
      }
    }

    return () => {
      channel.unsubscribe()
      resumeListener?.remove()
      document.removeEventListener('visibilitychange', handleVisibility)
    }
  }, [member.member_id])

  async function laadOngelezen() {
    const { data } = await supabase
      .from('messages').select('id')
      .eq('member_id', member.member_id)
      .eq('from_admin', true).eq('gelezen', false)
    setOngelezen(data?.length ?? 0)
  }

  // Berichten tab als aparte pagina — buiten app-shell
  if (activeTab === 'messages') {
    return (
      <BerichtenPagina
        member={memberData}
        onBack={() => setActiveTab('news')}
        onNavigate={setActiveTab}
        ongelezen={ongelezen}
        setOngelezen={setOngelezen}
      />
    )
  }

  return (
    <div className="app-shell">
      <div className="top-bar">
        <img src="/logo.png" alt="Vitaal Sterk" style={{ height: 38, width: 'auto', objectFit: 'contain' }} />
        <span style={{ fontSize: 10, color: 'rgba(255,255,255,0.75)', fontWeight: 600, fontFamily: 'Montserrat' }}>
          {memberData.name}
        </span>
      </div>

      <div className="page-content">
        {activeTab === 'news' && <HomeTab member={member} />}
        {activeTab === 'stempel' && <StempelkaartTab member={memberData} />}
        {activeTab === 'team' && <TeamTab />}
        {activeTab === 'profile' && <ProfileTab member={memberData} onSignOut={onSignOut} onUpdate={setMemberData} />}
      </div>

      <nav className="bottom-nav">
        {TABS.map(tab => (
          <button
            key={tab.id}
            className={activeTab === tab.id ? 'active' : ''}
            onClick={() => setActiveTab(tab.id)}
            style={{ position: 'relative' }}
          >
            {tab.icon}
            {tab.id === 'messages' && ongelezen > 0 && (
              <span style={{
                position: 'absolute', top: 4, right: '50%',
                transform: 'translateX(8px)',
                background: '#e76622', color: '#fff',
                borderRadius: '50%', width: 16, height: 16,
                fontSize: 9, fontWeight: 700,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontFamily: 'Montserrat', border: '1.5px solid #fff',
              }}>
                {ongelezen > 9 ? '9+' : ongelezen}
              </span>
            )}
            {tab.label}
          </button>
        ))}
      </nav>
    </div>
  )
}
